import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { FolderOpen, ImageIcon, Megaphone, Play, UserRound } from "lucide-react";
import { useCalmMotion } from "@/lib/studio";

function TiltTile({ to, label, cover, icon: Icon, badge, testId, overlay }) {
  const calm = useCalmMotion();
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotateX = useSpring(useTransform(y, [-0.5, 0.5], [9, -9]), { stiffness: 220, damping: 18 });
  const rotateY = useSpring(useTransform(x, [-0.5, 0.5], [-9, 9]), { stiffness: 220, damping: 18 });
  const move = (e) => {
    if (calm || e.pointerType !== "mouse") return;
    const r = e.currentTarget.getBoundingClientRect();
    x.set((e.clientX - r.left) / r.width - 0.5);
    y.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { x.set(0); y.set(0); };
  return (
    <Link to={to} data-testid={testId} className="group flex flex-col items-center gap-2.5 [perspective:900px]">
      <motion.div onPointerMove={move} onPointerLeave={reset} whileTap={calm ? undefined : { scale: 0.96 }} style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
        className="relative w-full rounded-[20px] border border-white/10 bg-ink-850 p-1.5 shadow-[0_14px_30px_-18px_rgba(0,0,0,0.9)] transition-colors group-hover:border-white/25">
        <div className="relative aspect-[5/4] overflow-hidden rounded-[15px] bg-ink-800 lg:aspect-[16/9]">
          {cover ? (
            <img src={cover} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-ink-700 to-ink-900 text-zinc-500"><Icon className="h-6 w-6" /></div>
          )}
          {overlay}
          <div className="pointer-events-none absolute inset-0 rounded-[15px] bg-gradient-to-tr from-transparent via-white/0 to-white/10 opacity-0 transition-opacity group-hover:opacity-100" style={{ transform: "translateZ(20px)" }} />
        </div>
        {badge && (
          <span className="absolute right-2.5 top-2.5 rounded-full border border-white/10 bg-black/70 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-zinc-200 backdrop-blur" style={{ transform: "translateZ(30px)" }}>
            {badge}
          </span>
        )}
      </motion.div>
      <span className="text-[13px] font-semibold tracking-tight text-white sm:text-sm">{label}</span>
    </Link>
  );
}

export function ShortcutTiles({ imageCover, videoCover, assetsCover, imageLocked, videoLocked, assetsCount, influencerCover, adsCover, proLocked }) {
  const play = <span className="absolute inset-0 flex items-center justify-center"><span className="flex h-8 w-8 items-center justify-center rounded-full bg-black/50 text-white backdrop-blur"><Play className="h-3.5 w-3.5 fill-current" /></span></span>;
  return (
    <nav aria-label="Creative tools" className="grid grid-cols-3 gap-3 sm:grid-cols-5 sm:gap-4" data-testid="shortcut-tiles">
      <TiltTile to="/create/image" label="Image" cover={imageCover} icon={ImageIcon} badge={imageLocked ? "Soon" : null} testId="shortcut-image" />
      <TiltTile to="/create/video" label="Video" cover={videoCover} icon={Play} badge={videoLocked ? "Soon" : null} testId="shortcut-video" overlay={play} />
      <TiltTile to="/create/influencer" label="Influencer" cover={influencerCover} icon={UserRound} badge={proLocked ? "Soon" : null} testId="shortcut-influencer" />
      <TiltTile to="/create/ads" label="Ads" cover={adsCover} icon={Megaphone} badge={proLocked ? "Soon" : null} testId="shortcut-ads" />
      <TiltTile to="/create/assets" label="Assets" cover={assetsCover} icon={FolderOpen} badge={assetsCount ? String(assetsCount) : null} testId="shortcut-assets" />
    </nav>
  );
}
